import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { trackBookingCompleted } from '../../utils/analytics';
import { useAuth0 } from '../../auth/AuthProvider';
import {
  consumePendingStripeReturn,
  type PendingStripeReturn,
} from '../../utils/pendingStripeReturn';

interface ThankYouLocationState {
  bookingId: number;
  value?: number;
  currency?: string;
  paymentPath?: 'stripe' | 'pay_later';
  tripId?: number;
  boatId?: number;
  paymentSucceeded?: boolean;
  paymentIntentId?: string | null;
}

const ThankYou: React.FC = () => {
  const routerState = useLocation().state as ThankYouLocationState | null;
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [pendingReturn, setPendingReturn] =
    useState<PendingStripeReturn | null>(null);
  const [hydrationDone, setHydrationDone] = useState(false);

  useEffect(() => {
    // Always consume so a successful in-app navigate cannot leave a stale
    // pending payload for a later bare /thankyou visit.
    setPendingReturn(consumePendingStripeReturn());
    setHydrationDone(true);
  }, []);

  const resolved = useMemo(() => {
    if (routerState?.bookingId) {
      return {
        bookingId: routerState.bookingId,
        value: routerState.value ?? 0,
        currency: routerState.currency ?? 'USD',
        paymentPath: routerState.paymentPath ?? 'pay_later',
        tripId: routerState.tripId,
        boatId: routerState.boatId,
        paymentSucceeded: routerState.paymentSucceeded !== false,
        paymentIntentId: routerState.paymentIntentId ?? null,
      };
    }

    const bookingIdFromQuery = Number(searchParams.get('bookingId'));
    const queryBookingId =
      Number.isFinite(bookingIdFromQuery) && bookingIdFromQuery > 0
        ? bookingIdFromQuery
        : null;
    const bookingId = queryBookingId ?? pendingReturn?.bookingId;

    if (!bookingId) {
      return null;
    }

    const matchingPending =
      pendingReturn?.bookingId === bookingId ? pendingReturn : null;

    const paymentSucceededParam = searchParams.get('paymentSucceeded');
    const redirectStatus = searchParams.get('redirect_status');
    const paymentSucceeded = !(
      redirectStatus === 'failed' ||
      redirectStatus === 'requires_payment_method' ||
      paymentSucceededParam === '0'
    );

    const paymentPathParam = searchParams.get('paymentPath');
    const paymentPath =
      paymentPathParam === 'pay_later' || paymentPathParam === 'stripe'
        ? paymentPathParam
        : (matchingPending?.paymentPath ?? 'stripe');
    const valueFromQuery = Number(searchParams.get('value'));
    const currencyFromQuery = searchParams.get('currency');

    return {
      bookingId,
      value: Number.isFinite(valueFromQuery)
        ? valueFromQuery
        : (matchingPending?.value ?? 0),
      currency: currencyFromQuery || matchingPending?.currency || 'USD',
      paymentPath,
      tripId:
        Number(searchParams.get('tripId')) ||
        matchingPending?.tripId ||
        undefined,
      boatId:
        Number(searchParams.get('boatId')) ||
        matchingPending?.boatId ||
        undefined,
      paymentSucceeded,
      paymentIntentId:
        searchParams.get('paymentIntentId') ||
        searchParams.get('payment_intent') ||
        matchingPending?.paymentIntentId ||
        null,
    };
  }, [routerState, searchParams, pendingReturn]);

  useEffect(() => {
    // Wait for sessionStorage consume so Stripe-redirect analytics gets the
    // real purchase value before dedupe marks the booking as tracked.
    if (!hydrationDone || !resolved?.bookingId || !resolved.paymentSucceeded) {
      return;
    }

    trackBookingCompleted({
      bookingId: resolved.bookingId,
      value: resolved.value,
      currency: resolved.currency,
      paymentPath: resolved.paymentPath,
      tripId: resolved.tripId,
      boatId: resolved.boatId,
    });
  }, [hydrationDone, resolved]);

  const handleCompleteDetails = () => {
    localStorage.setItem('redirectAfterLogin', '/app/tripmanagement');
    if (isAuthenticated) {
      window.location.assign('/app/tripmanagement');
      return;
    }
    loginWithRedirect();
  };

  return (
    <div className="bg-white text-center py-12 w-full justify-center items-center rounded-lg overflow-hidden flex flex-col px-6">
      <h1 className="text-xl md:text-3xl font-semibold text-blue-600">
        {resolved?.paymentSucceeded
          ? 'Woohoo, your booking is confirmed!'
          : 'Your spot is reserved — finish payment if needed'}
      </h1>
      <p className="text-md md:text-lg text-gray-600 mt-2">
        Booking ID: {resolved?.bookingId ?? '—'}
      </p>
      {resolved && !resolved.paymentSucceeded && (
        <p className="text-sm text-amber-700 mt-2 max-w-2xl">
          Payment did not complete. Keep this booking ID handy and contact{' '}
          <a href="mailto:help@santtai.com" className="underline">
            help@santtai.com
          </a>
          {resolved.paymentIntentId
            ? ` (payment reference ${resolved.paymentIntentId})`
            : ''}
          .
        </p>
      )}
      {!resolved?.bookingId && (
        <p className="text-sm text-amber-700 mt-2 max-w-2xl">
          We could not find your booking reference on this page. Check your
          confirmation email or contact{' '}
          <a href="mailto:help@santtai.com" className="underline">
            help@santtai.com
          </a>
          .
        </p>
      )}
      <p className="text-xl md:text-xl font-semibold text-gray-600 max-w-3xl mt-4">
        Next step: complete dive details for every guest — passport,
        certification, flights, and rental gear — so the boat can prepare for
        your trip.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={handleCompleteDetails}
          className="rounded-lg bg-gradient-to-r from-brand-santtai-primary to-brand-santtai-primary-darker px-6 py-3 font-semibold text-neutral-00"
        >
          Complete your dive details
        </button>
        <Link
          to="/"
          className="rounded-lg border border-neutral-300 px-6 py-3 font-semibold text-neutral-800"
        >
          Back to home
        </Link>
      </div>
      <img
        className="mt-6"
        width={500}
        src="https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/44a7a200-ba2a-4720-92af-268d36ba1800/public"
        alt="thank-you-image"
      />
      <div className="mt-6 text-gray-700">
        <p>Should you need any assistance or have questions,</p>
        <p>feel free to reach out to our dedicated support team</p>
        <p>
          via email at{' '}
          <a href="mailto:help@santtai.com" className="text-blue-600 underline">
            help@santtai.com
          </a>
        </p>
        <p>We&apos;re here to help!</p>
      </div>
    </div>
  );
};

export default ThankYou;
